import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    gradient: {
      primary: string;
      accent: string;
      soft: string;
    };
  }

  interface PaletteOptions {
    gradient?: {
      primary: string;
      accent: string;
      soft: string;
    };
  }

  interface Theme {
    customBackgrounds: {
      app: string;
      card: string;
      overlay: string;
    };
  }

  interface ThemeOptions {
    customBackgrounds?: {
      app: string;
      card: string;
      overlay: string;
    };
  }
}

const brandColors = {
  blue: '#2C7DFF',
  cobalt: '#1F62DD',
  deepBlue: '#2338C8',
  midnight: '#05060F',
  cyan: '#44D9FF',
  teal: '#1EA5D8',
  ice: '#BDEFFF',
  gold: '#D7B56A',
  goldSoft: '#F0D9A6',
  slate: '#94A3B8',
  navy: '#2D3460',
};

const typography = {
  fontFamily:
    '"Space Grotesk", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  h1: {
    fontWeight: 700,
    fontSize: '3rem',
    letterSpacing: '0.04em',
  },
  h2: {
    fontWeight: 600,
    fontSize: '2.25rem',
    letterSpacing: '0.03em',
  },
  h3: {
    fontWeight: 600,
    fontSize: '1.75rem',
    letterSpacing: '0.02em',
  },
  button: {
    textTransform: 'none' as const,
    fontWeight: 600,
    letterSpacing: '0.08em',
  },
  body1: {
    lineHeight: 1.7,
    fontSize: '1.05rem',
  },
  body2: {
    lineHeight: 1.6,
    fontSize: '0.95rem',
  },
};

const buildTheme = (mode: 'light' | 'dark') =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: brandColors.blue,
        light: brandColors.ice,
        dark: brandColors.deepBlue,
      },
      secondary: {
        main: brandColors.cyan,
        light: '#78E7FF',
        dark: brandColors.teal,
      },
      background: {
        default: mode === 'dark' ? '#090C16' : '#F6F7FB',
        paper: mode === 'dark' ? '#121629' : '#FFFFFF',
      },
      text: {
        primary: mode === 'dark' ? '#F8FBFF' : '#0C1124',
        secondary: mode === 'dark' ? '#B0B8D4' : '#4B5671',
      },
      gradient: {
        primary: `linear-gradient(135deg, ${brandColors.cyan} 0%, ${brandColors.blue} 58%, ${brandColors.deepBlue} 100%)`,
        accent: `linear-gradient(155deg, ${brandColors.cyan} 0%, ${brandColors.blue} 72%, ${brandColors.gold} 100%)`,
        soft: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.00) 80%)',
      },
    },
    customBackgrounds: {
      app:
        mode === 'dark'
          ? 'radial-gradient(circle at 20% 20%, rgba(44,125,255,0.2) 0%, transparent 45%), radial-gradient(circle at 80% 95%, rgba(215,181,106,0.12) 0%, transparent 38%), #05060F'
          : 'radial-gradient(circle at 20% 20%, rgba(44,125,255,0.14) 0%, transparent 45%), radial-gradient(circle at 80% 95%, rgba(215,181,106,0.08) 0%, transparent 38%), #F3F6FF',
      card: mode === 'dark' ? 'rgba(12,18,37,0.8)' : 'rgba(255,255,255,0.92)',
      overlay:
        'radial-gradient(circle at 70% 20%, rgba(68,217,255,0.24), transparent 55%)',
    },
    shape: {
      borderRadius: 18,
    },
    typography,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: mode === 'dark' ? '#05060F' : '#F7F7FA',
            backgroundImage:
              mode === 'dark'
                ? 'radial-gradient(circle at 15% 20%, rgba(44,125,255,0.14), transparent 45%), radial-gradient(circle at 80% 0%, rgba(68,217,255,0.18), transparent 50%), radial-gradient(circle at 72% 96%, rgba(215,181,106,0.1), transparent 35%)'
                : 'radial-gradient(circle at 15% 20%, rgba(44,125,255,0.09), transparent 45%), radial-gradient(circle at 80% 0%, rgba(68,217,255,0.15), transparent 55%), radial-gradient(circle at 72% 96%, rgba(215,181,106,0.08), transparent 35%)',
            minHeight: '100vh',
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: () => ({
            backgroundImage: 'none',
            backgroundColor: 'rgba(10,12,27,0.96)',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(18px)',
          }),
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            paddingLeft: '1.5rem',
            paddingRight: '1.5rem',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            paddingInline: '1.75rem',
          },
          containedPrimary: ({ theme }) => ({
            backgroundImage: theme.palette.gradient.primary,
            boxShadow: '0 10px 30px rgba(44,125,255,0.34)',
            color: '#fff',
            '&:hover': {
              backgroundImage: theme.palette.gradient.accent,
              boxShadow: '0 18px 45px rgba(31,98,221,0.42)',
            },
          }),
        },
        defaultProps: {
          disableElevation: true,
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.customBackgrounds.card,
            borderRadius: theme.shape.borderRadius,
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(16px)',
          }),
        },
      },
      MuiContainer: {
        defaultProps: {
          maxWidth: 'lg',
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            textDecoration: 'none',
            fontWeight: 600,
          },
        },
      },
    },
  });

export const lightTheme = buildTheme('light');
export const darkTheme = buildTheme('dark');
